import { NcwOnboardingRecoveryImage, useSetPageTopBarHeaderLeft } from '@capsicum/common';
import { Alert, BaseTextInput, Button, ButtonSection, Image, useCopyToClipboard } from '@capsicum/common-web';
import { CopyClipboardIcon, ShieldLockIcon } from '@capsicum/common-web/icons';
import { useRecoveryKitGenerated } from '@capsicum/features/nc-onboarding/shared';
import { useOnboardingAnalytics } from '@capsicum/socios-core';
import { type BaseTextInputIcon } from 'libs/common-web';
import styled from 'styled-components';

export type RecoveryKitGeneratedProps = {
	code: string;
	isReplacementKitAfterRecovery: boolean;
	keyId: string;
	onAccept: () => void;
	testIDPrefix: string;
};

export const RecoveryKitGenerated = ({
	code,
	isReplacementKitAfterRecovery,
	keyId,
	onAccept,
	testIDPrefix
}: RecoveryKitGeneratedProps) => {
	const intlTexts = useRecoveryKitGenerated({ isReplacementKitAfterRecovery });
	const { sendRegistrationRecoveryKitDownloadEvent } = useOnboardingAnalytics();

	// Remove back button from the top bar
	useSetPageTopBarHeaderLeft(null);

	const handleClickDownload = () => {
		!isReplacementKitAfterRecovery && sendRegistrationRecoveryKitDownloadEvent();
		const file = new Blob([`CODE:\n${code}\n\nKEYID:\n${keyId}`], { type: 'text/plain' });
		const downloadLink = document.createElement('a');

		downloadLink.href = URL.createObjectURL(file);
		downloadLink.target = '_blank';
		downloadLink.download = 'recovery-kit.txt';
		downloadLink.click();
	};

	const { copyToClipboard: copyRecoveryCodeToClipboard } = useCopyToClipboard({
		label: intlTexts.buttonCopySnack,
		value: code
	});

	const { copyToClipboard: copyRecoveryKeyIdToClipboard } = useCopyToClipboard({
		label: intlTexts.buttonCopySnack,
		value: keyId
	});

	const recoveryCodeCopyIcon: BaseTextInputIcon | undefined = {
		icon: () => <CopyClipboardIcon />,
		onClick: copyRecoveryCodeToClipboard,
		testID: `${testIDPrefix}-recoveryCode-copy`
	};

	const recoveryKeyIdCopyIcon: BaseTextInputIcon | undefined = {
		icon: () => <CopyClipboardIcon />,
		onClick: copyRecoveryKeyIdToClipboard,
		testID: `${testIDPrefix}-recoveryKeyId-copy`
	};

	return (
		<S.Container>
			<S.HeaderContainer>
				<S.ImageContainer>
					<S.StyledImage alt="Recovery" height={132} src={NcwOnboardingRecoveryImage} />
				</S.ImageContainer>
				<S.Title>{intlTexts.title}</S.Title>
			</S.HeaderContainer>
			<S.BodyContainer>
				<S.TextInputContainer>
					<S.TextInput
						iconEnd={recoveryCodeCopyIcon}
						label={intlTexts.inputRecoveryCode}
						readOnly
						testID={`${testIDPrefix}-recoveryCode-input`}
						value={code}
					/>
					<S.TextInput
						iconEnd={recoveryKeyIdCopyIcon}
						label={intlTexts.inputRecoveryKeyId}
						readOnly
						testID={`${testIDPrefix}-recoveryKeyId-input`}
						value={keyId}
					/>
				</S.TextInputContainer>
				<S.FullWidthAlert
					closable={false}
					icon={ShieldLockIcon}
					subTitle={intlTexts.alertSubtitle}
					testID="ncOnboarding-recoveryKit-alert"
					title={intlTexts.alertTitle}
					type="important"
				/>
				<Button
					color="brand"
					fullWidth
					testID={`${testIDPrefix}-downloadRecoveryKit-button`}
					text={intlTexts.buttonDownload}
					variant="outlined"
					onClick={handleClickDownload}
				>
					{intlTexts.buttonDownload}
				</Button>
			</S.BodyContainer>
			<ButtonSection>
				<Button
					color="brand"
					fullWidth
					testID={`${testIDPrefix}-registerContinue-button`}
					text={intlTexts.buttonContinue}
					variant="default"
					onClick={onAccept}
				/>
			</ButtonSection>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		width: 100%;
		display: flex;
		flex-flow: column;
		align-items: center;
		padding: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const HeaderContainer = styled.div`
		display: flex;
		width: 100%;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		row-gap: ${({ theme }) => theme.spacings.medium};
	`;

	export const Title = styled.div`
		${({ theme }) => theme.fonts.headings.title5};
		margin-bottom: ${({ theme }) => theme.spacings.large};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: center;
	`;

	export const BodyContainer = styled.div`
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		row-gap: ${({ theme }) => theme.spacings.large};
	`;

	export const ImageContainer = styled.div`
		width: 100%;
		max-width: 100px;
		display: flex;
		justify-content: center;
		align-items: center;
	`;

	export const StyledImage = styled(Image)`
		width: 100%;
		object-fit: contain;
	`;

	export const TextInputContainer = styled.div`
		display: flex;
		width: 100%;
		flex-direction: column;
		justify-content: center;
	`;

	export const TextInput = styled(BaseTextInput)`
		width: 100%;

		> div,
		> div:hover,
		> div:focus-within {
			background: ${({ theme }) => theme.colors.backgrounds.main.container};
			border: none;
		}
	`;

	export const FullWidthAlert = styled(Alert)`
		width: 100%;
	`;
}
